import { Box, Button, useToast } from "@chakra-ui/react";
import { Form, Formik, FormikHelpers } from "formik";
import React from "react";
import { useNavigate } from "react-router-dom";

import FormGroup from "../../../components/elements/formGroup";
import PhoneInput from "../../../components/elements/phoneInput";
import { GENERIC_SAVED_MESSAGE } from "../../../constants/lang/en";
import {
  CompanyFragmentFragment,
  useCompanyUpdateMutation,
} from "../../../graphql/graphql";
import { getRoutePath } from "../../../router";
import setServerErrors, {
  setGenericMessage,
} from "../../../utils/serverErrors";
import { phoneSchema, yupObject } from "../../../utils/validation";

interface CompanyPhoneChangePagePresenterProps {
  currentCompany: CompanyFragmentFragment;
}

const CompanyPhoneChangePagePresenter: React.FC<
  CompanyPhoneChangePagePresenterProps
> = ({ currentCompany }) => {
  const toast = useToast();
  const navigate = useNavigate();
  const [companyUpdateMutation, { loading }] = useCompanyUpdateMutation();

  const onSubmit = async (
    data: CompanyFormData,
    { setFieldError }: FormikHelpers<CompanyFormData>
  ) => {
    try {
      const { data: serverData, errors } = await companyUpdateMutation({
        variables: { data: { ...data, name: currentCompany.name } },
      });
      if (errors) {
        setServerErrors(errors, setFieldError);
      } else if (serverData) {
        toast({
          description: GENERIC_SAVED_MESSAGE,
          status: "success",
          isClosable: true,
          position: "top",
        });
        navigate(getRoutePath("companySettings"));
        return;
      }
    } catch (error) {
      toast({
        description: setGenericMessage(error),
        status: "error",
        position: "top",
        isClosable: true,
      });
    }
  };

  return (
    <Formik
      initialValues={{
        phone: currentCompany.phone || "",
      }}
      validationSchema={CompanyFormValidationSchema}
      onSubmit={onSubmit}
      enableReinitialize
    >
      {({ getFieldProps }) => (
        <Form noValidate>
          <FormGroup label="Phone" name="phone">
            <PhoneInput autoFocus autoComplete="tel" name="phone" />
          </FormGroup>
          <Box marginY="10">
            <Button width="full" type="submit" isLoading={loading}>
              Save
            </Button>
          </Box>
        </Form>
      )}
    </Formik>
  );
};

export default CompanyPhoneChangePagePresenter;

const CompanyFormValidationSchema = yupObject().shape({
  phone: phoneSchema,
});

type CompanyFormData = {
  phone: string;
};
